import React, { useState } from "react";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import { GrDown, GrUp } from "react-icons/gr";
import {
  black,
  grey,
  lightGrey,
  spacing,
  fontSize,
  fontWeight,
} from "../../theme";

const AccordionWrapper = styled.div({
  overflow: "hidden",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
});

const AccordionHeader = styled.div({
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.s,
  backgroundColor: lightGrey,
  borderTop: { style: "solid", color: black, width: "1px" },
  borderBottom: { style: "solid", color: black, width: "1px" },
  padding: { xy: spacing.xl },
  textAlign: "left",
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  ...media({ maxWidth: "768px" }, {
    padding: { xy: spacing.m },
    fontSize: fontSize.s,
  }),
  ":hover": {
    backgroundColor: grey,
  },
  ":focus": {
    outline: { style: "none" },
    backgroundColor: grey,
  },
});

const AccordionTitle = styled.div({
  minWidth: 0,
  flex: {
    grow: "1",
    shrink: "1",
    basis: "auto",
  },
  overflowWrap: "anywhere",
});

const AccordionContentWrapper = styled.div({
  display: "grid",
  gridTemplateRows: "0fr",
  transitionDuration: "0.3s",
  transitionProperty: "grid-template-rows",
  transitionTimingFunction: "ease",
});
const AccordionContentWrapperOpen = styled({
  gridTemplateRows: "1fr",
});
const AccordionContentInner = styled.div({
  minHeight: 0,
  overflow: "hidden",
  padding: { xy: "0px" },
});
const AccordionContentInnerOpen = styled({
  padding: { xy: spacing.m },
});

type AccordionProps = {
  title: string;
  children: React.ReactNode;
  /** Initial open (uncontrolled) eller styrt värde tillsammans med onToggle. */
  open?: boolean;
  /** Om satt: controlled accordion — parent äger open-state. */
  onToggle?: (open: boolean) => void;
};

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  open = false,
  onToggle,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(open);
  const isControlled = typeof onToggle === "function";
  const isOpen = isControlled ? open : uncontrolledOpen;

  const toggleAccordion = () => {
    const next = !isOpen;
    if (isControlled) onToggle(next);
    else setUncontrolledOpen(next);
  };

  return (
    <AccordionWrapper>
      <AccordionHeader onClick={toggleAccordion}>
        <AccordionTitle>{title}</AccordionTitle>
        <div>{isOpen ? <GrUp /> : <GrDown />}</div>
      </AccordionHeader>
      <AccordionContentWrapper css={isOpen && AccordionContentWrapperOpen}>
        <AccordionContentInner css={isOpen && AccordionContentInnerOpen}>
          {children}
        </AccordionContentInner>
      </AccordionContentWrapper>
    </AccordionWrapper>
  );
};

export default Accordion;
